import { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo, startTransition } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LucideIcon, Sun } from 'lucide-react'
import { DailyIcon } from '../../components/icons/DailyIcon'
import { useTabPersistence } from './useTabPersistence'
import { deserializeTab, SerializedTab } from './tabSerialization'
import { db } from '../../lib/db/indexeddb'
import { markTabEvent } from '../../lib/perf/tabProfiling'

/**
 * Represents a single browser-style tab within a Life Space.
 *
 * @property id - Unique identifier (typically `<space>-<name>-<timestamp>`)
 * @property path - React Router path the tab navigates to (e.g. `'/home/overview'`)
 * @property title - Display title shown in the tab bar
 * @property icon - Optional Lucide icon component rendered next to the title
 * @property lifeSpace - ID of the Life Space this tab belongs to
 * @property isDirty - Whether the tab has unsaved changes (shows a dot indicator)
 * @property isPinned - Whether the tab is pinned (pinned tabs resist close and reorder)
 * @property isPreview - Whether this is a VS Code-style preview tab (italic title, replaced on next single-click)
 */
export interface Tab {
  id: string
  path: string
  title: string
  icon?: LucideIcon  // React component, not string!
  lifeSpace: string
  isDirty?: boolean
  isPinned?: boolean
  isPreview?: boolean
}

/** Default tab configs without translated titles (used as fallback structure). */
const DEFAULT_TAB_CONFIGS: Record<string, { id: string; path: string; titleKey: string; icon: LucideIcon; lifeSpace: string }> = {
  'all': { id: 'all-daily', path: '/home/overview', titleKey: 'tabs.defaultAll', icon: DailyIcon as LucideIcon, lifeSpace: 'all' },
  'personal': { id: 'personal-today', path: '/home/personal/today', titleKey: 'tabs.defaultPersonal', icon: Sun, lifeSpace: 'personal' },
  'work': { id: 'work-today', path: '/home/work/today', titleKey: 'tabs.defaultWork', icon: Sun, lifeSpace: 'work' },
  'home': { id: 'home-today', path: '/home/home/today', titleKey: 'tabs.defaultHome', icon: Sun, lifeSpace: 'home' },
}

/** Entry in a tab's navigation history (back/forward stacks). */
interface TabHistoryEntry {
  path: string
  title: string
  icon?: LucideIcon
}

/**
 * Hook for managing tabs across all Life Spaces with per-space persistence.
 *
 * ⚠️ SINGLETON HOOK — must be called **exactly once** in the app tree
 * (currently inside `MainLayout`). All downstream consumers should access
 * the state via `useTabContext()` from `TabManagerContext`. Calling this
 * hook twice creates two independent state copies and will cause subtle
 * bugs (e.g. divergent `activeTabId`, split tab lists, lost navigation).
 *
 * @description
 * Keeps tabs for ALL Life Spaces in memory simultaneously. The TabBar only
 * renders the active space's tabs (`visibleTabs`). Switching spaces is instant
 * — no destroy/restore cycle, no race conditions.
 *
 * Persistence: Each space is saved separately to localStorage (`tab_session_{spaceId}`).
 * Secondary windows (tear-off) receive tabs via IPC and skip persistence to
 * avoid overwriting the primary window's session.
 *
 * @returns An object containing:
 * - `tabs` — Visible tabs for the current Life Space
 * - `activeTab` — Currently active tab (or first visible tab as fallback)
 * - `isSecondaryWindow` — Whether this is a tear-off window
 * - `secondaryReady` — Whether secondary window has finished initialization
 * - `switchToSpace` — Switch to a different Life Space by ID
 * - `openTab` — Open a new tab or activate an existing one with the same path
 * - `closeTab` — Close a tab (with pinned-tab confirmation)
 * - `setActiveTab` — Activate a tab and navigate to its path
 * - `pinTab` — Toggle pin state on a tab
 * - `updateTab` — Partially update a tab's fields
 * - `duplicateTab` — Create a copy of a tab
 * - `closeOtherTabs` — Close all tabs except the specified one (preserves pinned)
 * - `closeTabsToRight` — Close all tabs to the right (preserves pinned)
 * - `closeTabsToLeft` — Close all tabs to the left (preserves pinned)
 * - `closeAllTabs` — Close all tabs in the active space (preserves pinned)
 * - `closeMultipleTabs` — Batch-close multiple tabs for better performance
 * - `reorderTabs` — Move a tab from one index to another within the active space
 *
 * @example
 * const { tabs, activeTab, openTab, closeTab, switchToSpace } = useTabManager()
 *
 * @example
 * // Open a new tab
 * openTab({ id: 'tasks-1', path: '/home/tasks', title: 'Tasks', lifeSpace: 'personal' })
 *
 * @example
 * // Switch to another Life Space
 * switchToSpace('work')
 */
// Dev-mode singleton guard — throws loudly if useTabManager is called more
// than once. Production skips the check to avoid any runtime cost.
let __useTabManagerInstanceCount = 0

export const useTabManager = () => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      __useTabManagerInstanceCount += 1
      if (__useTabManagerInstanceCount > 1) {
        console.error(
          '[useTabManager] Called more than once (' + __useTabManagerInstanceCount + ' instances). ' +
          'This hook MUST be a singleton — consumers should use useTabContext() instead.'
        )
      }
      return () => { __useTabManagerInstanceCount -= 1 }
    }, [])
  }

  const [tabs, setTabs] = useState<Tab[]>([])
  const [activeTabId, setActiveTabId] = useState<string | null>(null)
  const { saveSession, restoreSession, getSessionActiveTabId } = useTabPersistence()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const getDefaultTabForLifeSpace = useCallback((spaceId: string): Tab | null => {
    const config = DEFAULT_TAB_CONFIGS[spaceId]
    if (!config) return null
    return {
      id: config.id,
      path: config.path,
      title: t(config.titleKey),
      icon: config.icon,
      lifeSpace: config.lifeSpace,
      isPreview: false,
    }
  }, [t])

  // Own tracking of active space — read directly from localStorage to avoid
  // useLifeSpaces' async initialization (useState('personal') → useEffect → real value).
  // Updated by switchToSpace(), not dependent on useLifeSpaces.
  const [currentSpace, setCurrentSpace] = useState(() => {
    return localStorage.getItem('activeLifeSpace') || 'personal'
  })

  // In-memory tracking of last active tab per space (survives debounce delays)
  const activeTabPerSpaceRef = useRef<Record<string, string | null>>({})

  // Per-tab navigation history (back/forward stacks for Cmd+Arrow navigation)
  const tabHistoryRef = useRef<Record<string, { back: TabHistoryEntry[]; forward: TabHistoryEntry[] }>>({})

  // Per-space preview tab tracking (at most one preview tab per Life Space)
  const previewTabPerSpaceRef = useRef<Record<string, string | null>>({})

  // Secondary windows (tear-off) have a windowId URL param — they get tabs via IPC only
  const isSecondaryWindow = useRef(
    new URLSearchParams(window.location.hash.split('?')[1] || '').has('windowId')
  )

  // Track whether secondary window has finished pulling its init data
  const [secondaryReady, setSecondaryReady] = useState(!isSecondaryWindow.current)

  // Visible tabs = only the current space's tabs
  const visibleTabs = useMemo(() => {
    return tabs.filter(t => t.lifeSpace === currentSpace)
  }, [tabs, currentSpace])

  // Active tab must belong to the current space
  const activeTab = visibleTabs.find(t => t.id === activeTabId) || visibleTabs[0]

  // Keep per-space active tab tracking in sync
  if (activeTab && currentSpace) {
    activeTabPerSpaceRef.current[currentSpace] = activeTab.id
  }

  // Ref mirrors — always up-to-date, avoids stale closures in openTab
  const tabsRef = useRef(tabs)
  tabsRef.current = tabs
  const activeTabIdRef = useRef(activeTabId)
  activeTabIdRef.current = activeTabId

  const openTab = useCallback((tab: Tab) => {
    // Mark the user-intent moment so the Chrome timeline's `tab:commit` and
    // `tab:paint` spans anchor to it. Both the existing-tab (re-activate) and
    // new-tab paths activate `tab.path`, so the mark is safe up-front here.
    markTabEvent('switch-request', tab.path)
    // Read from ref to avoid stale closure when called rapidly
    const existing = tabsRef.current.find(t => t.path === tab.path && t.lifeSpace === tab.lifeSpace)

    if (existing) {
      // If the existing tab is a preview, promote it to permanent
      if (existing.isPreview) {
        if (previewTabPerSpaceRef.current[existing.lifeSpace] === existing.id) {
          previewTabPerSpaceRef.current[existing.lifeSpace] = null
        }
        setTabs(prev => prev.map(t =>
          t.id === existing.id ? { ...t, isPreview: false } : t
        ))
      }
      setActiveTabId(existing.id)
      startTransition(() => navigate(existing.path))
    } else {
      const space = tab.lifeSpace
      const previewId = space ? previewTabPerSpaceRef.current[space] : null

      // Promote existing preview tab to permanent
      if (previewId) {
        previewTabPerSpaceRef.current[space] = null
      }
      setTabs(prev => {
        const promoted = previewId
          ? prev.map(t => t.id === previewId ? { ...t, isPreview: false } : t)
          : prev
        return [...promoted, tab]
      })
      setActiveTabId(tab.id)
      startTransition(() => navigate(tab.path))
    }
  }, [navigate])

  const openPreviewTab = useCallback((tab: Omit<Tab, 'id'> & { id?: string }) => {
    const space = tab.lifeSpace

    // If path already open as permanent tab → activate it and close preview
    const permanentTab = tabsRef.current.find(
      t => t.path === tab.path && t.lifeSpace === space && !t.isPreview
    )
    if (permanentTab) {
      // Auto-close preview tab when activating a permanent tab
      const previewId = previewTabPerSpaceRef.current[space]
      if (previewId && previewId !== permanentTab.id) {
        previewTabPerSpaceRef.current[space] = null
        delete tabHistoryRef.current[previewId]
        setTabs(prev => prev.filter(t => t.id !== previewId))
      }
      setActiveTabId(permanentTab.id)
      startTransition(() => navigate(permanentTab.path))
      return
    }

    const currentPreviewId = previewTabPerSpaceRef.current[space]
    const currentPreview = currentPreviewId
      ? tabsRef.current.find(t => t.id === currentPreviewId)
      : null

    if (currentPreview) {
      // Preview already showing this path → just activate
      if (currentPreview.path === tab.path) {
        setActiveTabId(currentPreview.id)
        startTransition(() => navigate(currentPreview.path))
        return
      }

      // Navigate preview in-place (same logic as navigateActiveTab but for a specific tab)
      const hist = tabHistoryRef.current[currentPreview.id] ||= { back: [], forward: [] }
      hist.back.push({ path: currentPreview.path, title: currentPreview.title, icon: currentPreview.icon })
      hist.forward = []

      flushSync(() => {
        setTabs(prev => prev.map(t =>
          t.id === currentPreview.id
            ? { ...t, path: tab.path, title: tab.title, icon: tab.icon }
            : t
        ))
      })
      setActiveTabId(currentPreview.id)
      startTransition(() => navigate(tab.path))
      return
    }

    // Single tab in space: navigate it in-place (looks permanent, behaves like preview)
    const spaceTabs = tabsRef.current.filter(t => t.lifeSpace === space)
    if (spaceTabs.length === 1 && !spaceTabs[0].isPinned) {
      const soloTab = spaceTabs[0]
      if (soloTab.path === tab.path) {
        setActiveTabId(soloTab.id)
        startTransition(() => navigate(soloTab.path))
        return
      }
      const hist = tabHistoryRef.current[soloTab.id] ||= { back: [], forward: [] }
      hist.back.push({ path: soloTab.path, title: soloTab.title, icon: soloTab.icon })
      hist.forward = []
      flushSync(() => {
        setTabs(prev => prev.map(t =>
          t.id === soloTab.id
            ? { ...t, path: tab.path, title: tab.title, icon: tab.icon }
            : t
        ))
      })
      setActiveTabId(soloTab.id)
      startTransition(() => navigate(tab.path))
      return
    }

    // Multiple tabs, no preview → create new preview tab
    const newTab: Tab = {
      id: tab.id || `${space}-preview-${Date.now()}`,
      path: tab.path,
      title: tab.title,
      icon: tab.icon,
      lifeSpace: space,
      isDirty: false,
      isPinned: false,
      isPreview: true
    }
    previewTabPerSpaceRef.current[space] = newTab.id
    setTabs(prev => [...prev, newTab])
    setActiveTabId(newTab.id)
    startTransition(() => navigate(newTab.path))
  }, [navigate])

  const closeTab = useCallback((tab: Tab) => {
    // Don't close the last remaining tab
    if (tabsRef.current.length <= 1) return

    delete tabHistoryRef.current[tab.id]
    // Clear preview tracking if closing a preview tab
    if (tab.isPreview && previewTabPerSpaceRef.current[tab.lifeSpace] === tab.id) {
      previewTabPerSpaceRef.current[tab.lifeSpace] = null
    }
    const currentTabs = tabsRef.current
    const newTabs = currentTabs.filter(t => t.id !== tab.id)
    setTabs(newTabs)

    // If we closed the active tab, activate another one from the same space
    if (activeTabId === tab.id) {
      const spaceTabs = newTabs.filter(t => t.lifeSpace === tab.lifeSpace)
      const index = currentTabs.filter(t => t.lifeSpace === tab.lifeSpace).findIndex(t => t.id === tab.id)
      const newActiveTab = spaceTabs[Math.min(index, spaceTabs.length - 1)] || spaceTabs[0]
      setActiveTabId(newActiveTab?.id || null)
      if (newActiveTab) startTransition(() => navigate(newActiveTab.path))
    }
  }, [activeTabId, navigate, t])

  const setActiveTab = useCallback((tab: Tab) => {
    // Mark the user-intent moment so the Chrome timeline's `tab:commit` and
    // `tab:paint` spans anchor to it. See `src/renderer/lib/perf/tabProfiling.ts`.
    markTabEvent('switch-request', tab.path)
    // Auto-close preview tab when clicking a permanent tab
    if (!tab.isPreview) {
      const space = tab.lifeSpace
      const previewId = previewTabPerSpaceRef.current[space]
      if (previewId && previewId !== tab.id) {
        previewTabPerSpaceRef.current[space] = null
        delete tabHistoryRef.current[previewId]
        setTabs(prev => prev.filter(t => t.id !== previewId))
      }
    }
    setActiveTabId(tab.id)
    startTransition(() => navigate(tab.path))
  }, [navigate])

  const pinTab = useCallback((tab: Tab) => {
    // Pinning a preview tab promotes it first
    if (tab.isPreview) {
      if (previewTabPerSpaceRef.current[tab.lifeSpace] === tab.id) {
        previewTabPerSpaceRef.current[tab.lifeSpace] = null
      }
    }
    setTabs(prev => {
      const wasPinned = prev.find(t => t.id === tab.id)?.isPinned
      if (wasPinned) {
        // Unpinning: just toggle, keep position
        return prev.map(t =>
          t.id === tab.id ? { ...t, isPinned: false } : t
        )
      }
      // Pinning: move tab to end of pinned group
      const without = prev.filter(t => t.id !== tab.id)
      const lastPinnedIdx = without.reduce((acc, t, i) => t.isPinned ? i : acc, -1)
      const updated = { ...tab, isPinned: true, isPreview: false }
      without.splice(lastPinnedIdx + 1, 0, updated)
      return without
    })
  }, [])

  const updateTab = useCallback((tabId: string, updates: Partial<Tab>) => {
    setTabs(prev => prev.map(t =>
      t.id === tabId ? { ...t, ...updates } : t
    ))
  }, [])

  const navigateActiveTab = useCallback((updates: { path: string; title: string; icon?: LucideIcon }) => {
    // Read current active tab from refs to avoid stale closure issues.
    // Without this, rapid tab switching can cause navigateActiveTab to mutate
    // the WRONG tab (the previously active one) with the current tab's path data.
    const currentTab = tabsRef.current.find(t => t.id === activeTabIdRef.current)
    if (!currentTab) return
    // Don't navigate if already on the same path
    if (currentTab.path === updates.path) return
    // If another tab already shows this path, switch to it instead
    const existing = tabsRef.current.find(t => t.id !== currentTab.id && t.path === updates.path)
    if (existing) {
      setActiveTabId(existing.id)
      startTransition(() => navigate(existing.path))
      return
    }
    // Pinned tabs cannot change their content — open a new tab instead
    if (currentTab.isPinned) {
      const newTab: Tab = {
        id: `${updates.path}-${Date.now()}`,
        path: updates.path,
        title: updates.title,
        icon: updates.icon,
        lifeSpace: currentTab.lifeSpace,
        isDirty: false,
        isPinned: false
      }
      setTabs(prev => [...prev, newTab])
      setActiveTabId(newTab.id)
      startTransition(() => navigate(newTab.path))
      return
    }
    // Push current state onto back stack, clear forward stack
    const hist = tabHistoryRef.current[currentTab.id] ||= { back: [], forward: [] }
    hist.back.push({ path: currentTab.path, title: currentTab.title, icon: currentTab.icon })
    hist.forward = []
    // flushSync ensures React processes the tab state update synchronously
    // before navigate() triggers a Router re-render (fixes Web/BrowserRouter)
    flushSync(() => {
      setTabs(prev => prev.map(t =>
        t.id === currentTab.id ? { ...t, ...updates } : t
      ))
    })
    startTransition(() => navigate(updates.path))
  }, [navigate])

  const promotePreviewTab = useCallback((tabId: string) => {
    const tab = tabsRef.current.find(t => t.id === tabId)
    if (!tab || !tab.isPreview) return

    // Clear preview tracking for this space
    if (previewTabPerSpaceRef.current[tab.lifeSpace] === tabId) {
      previewTabPerSpaceRef.current[tab.lifeSpace] = null
    }

    setTabs(prev => prev.map(t =>
      t.id === tabId ? { ...t, isPreview: false } : t
    ))
  }, [])

  const promotePreviewBySpace = useCallback((lifeSpace: string) => {
    const previewTabId = previewTabPerSpaceRef.current[lifeSpace]
    if (!previewTabId) return

    previewTabPerSpaceRef.current[lifeSpace] = null

    // Functional updater guarantees latest state even during batched updates
    setTabs(prev => prev.map(t =>
      t.id === previewTabId ? { ...t, isPreview: false } : t
    ))
  }, [])

  const goBack = useCallback(() => {
    const currentTab = tabsRef.current.find(t => t.id === activeTabIdRef.current)
    if (!currentTab) return
    const hist = tabHistoryRef.current[currentTab.id]
    if (!hist || hist.back.length === 0) return
    const prev = hist.back.pop()!
    hist.forward.push({ path: currentTab.path, title: currentTab.title, icon: currentTab.icon })
    flushSync(() => {
      setTabs(tabs => tabs.map(t =>
        t.id === currentTab.id ? { ...t, ...prev } : t
      ))
    })
    startTransition(() => navigate(prev.path))
  }, [navigate])

  const goForward = useCallback(() => {
    const currentTab = tabsRef.current.find(t => t.id === activeTabIdRef.current)
    if (!currentTab) return
    const hist = tabHistoryRef.current[currentTab.id]
    if (!hist || hist.forward.length === 0) return
    const next = hist.forward.pop()!
    hist.back.push({ path: currentTab.path, title: currentTab.title, icon: currentTab.icon })
    flushSync(() => {
      setTabs(tabs => tabs.map(t =>
        t.id === currentTab.id ? { ...t, ...next } : t
      ))
    })
    startTransition(() => navigate(next.path))
  }, [navigate])

  const canGoBack = activeTab ? (tabHistoryRef.current[activeTab.id]?.back.length ?? 0) > 0 : false
  const canGoForward = activeTab ? (tabHistoryRef.current[activeTab.id]?.forward.length ?? 0) > 0 : false

  const duplicateTab = useCallback((tab: Tab) => {
    const newTab: Tab = {
      ...tab,
      id: `${tab.id}-copy-${Date.now()}`,
      title: t('tabs.copyFormat', { title: tab.title }),
      isPinned: false,
      isDirty: false
    }
    openTab(newTab)
  }, [openTab, t])

  const closeOtherTabs = useCallback((keepTab: Tab) => {
    // Only close other tabs in the SAME space
    setTabs(prev => prev.filter(t =>
      t.lifeSpace !== keepTab.lifeSpace || t.id === keepTab.id || t.isPinned
    ))
    if (activeTabId && activeTabId !== keepTab.id) {
      setActiveTabId(keepTab.id)
    }
  }, [activeTabId])

  const closeTabsToRight = useCallback((fromTab: Tab) => {
    const spaceTabs = tabsRef.current.filter(t => t.lifeSpace === fromTab.lifeSpace)
    const index = spaceTabs.findIndex(t => t.id === fromTab.id)
    const idsToRemove = new Set(
      spaceTabs.filter((t, i) => i > index && !t.isPinned).map(t => t.id)
    )
    setTabs(prev => prev.filter(t => !idsToRemove.has(t.id)))
  }, [])

  const closeTabsToLeft = useCallback((fromTab: Tab) => {
    const spaceTabs = tabsRef.current.filter(t => t.lifeSpace === fromTab.lifeSpace)
    const index = spaceTabs.findIndex(t => t.id === fromTab.id)
    const idsToRemove = new Set(
      spaceTabs.filter((t, i) => i < index && !t.isPinned).map(t => t.id)
    )
    setTabs(prev => prev.filter(t => !idsToRemove.has(t.id)))
  }, [])

  const closeAllTabs = useCallback(() => {
    // Only close tabs in the active space
    setTabs(prev => prev.filter(t =>
      t.lifeSpace !== currentSpace || t.isPinned
    ))
    const firstPinnedTab = tabsRef.current.find(t => t.lifeSpace === currentSpace && t.isPinned)
    setActiveTabId(firstPinnedTab?.id || null)
  }, [currentSpace])

  // Batch close multiple tabs for better performance
  const closeMultipleTabs = useCallback((tabsToClose: Tab[]) => {
    const tabIdsToClose = new Set(tabsToClose.map(t => t.id))
    tabIdsToClose.forEach(id => delete tabHistoryRef.current[id])

    setTabs(prev => {
      const remaining = prev.filter(t => !tabIdsToClose.has(t.id))

      // If active tab was closed, switch to next available tab in same space
      if (activeTabId && tabIdsToClose.has(activeTabId)) {
        const spaceTabs = remaining.filter(t => t.lifeSpace === currentSpace)
        const nextTab = spaceTabs[0]
        if (nextTab) {
          setActiveTabId(nextTab.id)
          startTransition(() => navigate(nextTab.path))
        }
      }

      return remaining
    })
  }, [activeTabId, currentSpace, navigate])

  const reorderTabs = useCallback((fromIndex: number, toIndex: number) => {
    // flushSync ensures the DOM updates synchronously before DnD Kit's
    // post-drag cleanup runs. Without it, DnD Kit may snapshot stale
    // positions and animate tabs back to their pre-reorder slots (web only).
    // Same pattern used by navigateActiveTab/goBack/goForward.
    flushSync(() => {
      setTabs(prev => {
        const spaceTabs = prev.filter(t => t.lifeSpace === currentSpace)
        const otherTabs = prev.filter(t => t.lifeSpace !== currentSpace)
        const reordered = [...spaceTabs]
        const [removed] = reordered.splice(fromIndex, 1)
        reordered.splice(toIndex, 0, removed)
        return [...otherTabs, ...reordered]
      })
    })
  }, [currentSpace])

  // Switch to a different life space — activates the last active tab of that space
  const switchToSpace = useCallback((spaceId: string) => {
    // Save current active tab for current space before switching
    if (activeTabId && currentSpace) {
      activeTabPerSpaceRef.current[currentSpace] = activeTabId
    }

    setCurrentSpace(spaceId)

    const spaceTabs = tabsRef.current.filter(t => t.lifeSpace === spaceId)

    if (spaceTabs.length === 0) {
      // No tabs for this space → open default
      const defaultTab = getDefaultTabForLifeSpace(spaceId)
      if (defaultTab) {
        const newTab = { ...defaultTab, id: `${defaultTab.id}-${Date.now()}` }
        setTabs(prev => [...prev, newTab])
        setActiveTabId(newTab.id)
        startTransition(() => navigate(newTab.path))
      }
    } else {
      // Activate last known active tab: in-memory first, localStorage fallback
      const memorizedActiveId = activeTabPerSpaceRef.current[spaceId]
      const tabToActivate = spaceTabs.find(t => t.id === memorizedActiveId) || spaceTabs[0]
      setActiveTabId(tabToActivate.id)
      startTransition(() => navigate(tabToActivate.path))
    }
  }, [navigate, getDefaultTabForLifeSpace])

  // Stable ref for navigate to avoid triggering effects on navigate changes
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  // Restore ALL space sessions on mount (only once).
  // IMPORTANT: Must be useLayoutEffect so it fires BEFORE <Navigate>'s useEffect
  // in HomeLayout. This prevents a race condition where Navigate redirects to
  // /home/overview before tab restoration can navigate to the saved route.
  // Secondary windows (tear-off) skip this — they receive tabs via window:init IPC
  useLayoutEffect(() => {
    if (isSecondaryWindow.current) return

    // Collect all known space IDs
    const allSpaceIds = ['all', 'personal', 'work', 'home']
    try {
      const customSpacesStr = localStorage.getItem('customLifeSpaces')
      if (customSpacesStr) {
        const customSpaces = JSON.parse(customSpacesStr) as Array<{ id?: string }>
        customSpaces.forEach((s) => {
          if (s.id) allSpaceIds.push(s.id)
        })
      }
    } catch (e) {
      console.warn('Failed to parse custom life spaces from localStorage:', e)
    }

    // Restore tabs from all spaces and initialize per-space active tab tracking
    const allRestoredTabs: Tab[] = []
    allSpaceIds.forEach(spaceId => {
      const restoredTabs = restoreSession(spaceId)
      if (restoredTabs && restoredTabs.length > 0) {
        const uniqueTabs = restoredTabs.map((tab, index) => ({
          ...tab,
          id: `${tab.id}-${Date.now()}-${index}`
        }))
        allRestoredTabs.push(...uniqueTabs)

        // Initialize per-space active tab from saved session
        const savedActiveId = getSessionActiveTabId(spaceId)
        if (savedActiveId) {
          const matchingTab = uniqueTabs.find(t => t.id.startsWith(savedActiveId))
          if (matchingTab) {
            activeTabPerSpaceRef.current[spaceId] = matchingTab.id
          }
        }

        // Initialize per-space preview tab tracking from restored tabs
        const previewTab = uniqueTabs.find(t => t.isPreview)
        if (previewTab) {
          previewTabPerSpaceRef.current[spaceId] = previewTab.id
        }
      }
    })

    // Ensure the active space has at least one tab
    if (!allRestoredTabs.some(t => t.lifeSpace === currentSpace)) {
      const defaultTab = getDefaultTabForLifeSpace(currentSpace)
      if (defaultTab) {
        const uniqueTab = { ...defaultTab, id: `${defaultTab.id}-${Date.now()}` }
        allRestoredTabs.push(uniqueTab)
      }
    }

    if (allRestoredTabs.length > 0) {
      setTabs(allRestoredTabs)
      // Activate the last active tab of the current space
      const savedActiveId = getSessionActiveTabId(currentSpace)
      const activeSpaceTabs = allRestoredTabs.filter(t => t.lifeSpace === currentSpace)
      const activeTabToRestore = activeSpaceTabs.find(t =>
        t.id.startsWith(savedActiveId || '')
      ) || activeSpaceTabs[0]
      if (activeTabToRestore) {
        setActiveTabId(activeTabToRestore.id)
        navigateRef.current(activeTabToRestore.path, { replace: true })
      }
    } else {
      // No sessions at all — open default for active space
      const defaultTab = getDefaultTabForLifeSpace(currentSpace)
      if (defaultTab) {
        const uniqueTab = { ...defaultTab, id: `${defaultTab.id}-${Date.now()}` }
        setTabs([uniqueTab])
        setActiveTabId(uniqueTab.id)
        navigateRef.current(uniqueTab.path, { replace: true })
      }
    }
  }, []) // Only on mount

  // Secondary windows: pull init data from main process on mount
  useEffect(() => {
    if (!isSecondaryWindow.current) return
    if (!window.windowAPI?.getWindowInit) return

    const params = new URLSearchParams(window.location.hash.split('?')[1] || '')
    const windowId = params.get('windowId')
    if (!windowId) return

    window.windowAPI.getWindowInit(windowId).then((data: {
      windowId: string
      initialTabs?: SerializedTab[]
      uiState?: { sidebarLeft: boolean; sidebarRight: boolean }
    } | null) => {
      if (data?.initialTabs && data.initialTabs.length > 0) {
        const restoredTabs = data.initialTabs.map(deserializeTab)
        setTabs(restoredTabs)
        setActiveTabId(restoredTabs[0].id)
        navigateRef.current(restoredTabs[0].path)
      }
      setSecondaryReady(true)
    }).catch((err: unknown) => {
      console.error('Failed to get window init data:', err)
      setSecondaryReady(true)
    })
  }, [])

  // Listen for tab:drop IPC (merge: another window drops a tab into this one)
  // Receives tab data + cursor position for position-aware insertion
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('tab:drop', (tabData: SerializedTab, cursor?: { x: number; y: number }) => {
      const tab = deserializeTab(tabData)
      // Avoid duplicates (same path already open in same space)
      const existing = tabsRef.current.find(t => t.path === tab.path && t.lifeSpace === tab.lifeSpace)
      if (existing) {
        setActiveTabId(existing.id)
        navigateRef.current(existing.path)
      } else {
        setTabs(prev => {
          // Calculate drop index within visible tabs
          let dropIdx = prev.filter(t => t.lifeSpace === currentSpace).length
          if (cursor) {
            const container = document.querySelector('.tab-bar__tabs') as HTMLElement
            if (container) {
              const rect = container.getBoundingClientRect()
              const localX = cursor.x - window.screenX - rect.left
              const tabEls = container.querySelectorAll('.tab-bar__tab')
              for (let i = 0; i < tabEls.length; i++) {
                const tabEl = tabEls[i] as HTMLElement
                const tabCenter = tabEl.offsetLeft + tabEl.offsetWidth / 2
                if (localX < tabCenter) {
                  dropIdx = i
                  break
                }
              }
            }
          }
          // Insert among same-space tabs at the right position
          const spaceTabs = prev.filter(t => t.lifeSpace === currentSpace)
          const otherTabs = prev.filter(t => t.lifeSpace !== currentSpace)
          spaceTabs.splice(dropIdx, 0, tab)
          return [...otherTabs, ...spaceTabs]
        })
        setActiveTabId(tab.id)
        navigateRef.current(tab.path)
      }
    })
    return () => { unsubscribe?.() }
  }, [currentSpace])

  // Listen for tab:remove IPC (main process tells us to remove a tab after tear-off/merge)
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('tab:remove', (tabId: string) => {
      setTabs(prev => {
        // Don't remove the last tab of the active space in primary window
        const activeSpaceTabs = prev.filter(t => t.lifeSpace === currentSpace)
        if (activeSpaceTabs.length <= 1 && !isSecondaryWindow.current &&
            activeSpaceTabs.some(t => t.id === tabId)) {
          return prev
        }
        const newTabs = prev.filter(t => t.id !== tabId)
        // If we removed the active tab, switch to another in the same space
        if (activeTabId === tabId && newTabs.length > 0) {
          const spaceTabs = newTabs.filter(t => t.lifeSpace === currentSpace)
          const nextTab = spaceTabs[0]
          if (nextTab) {
            setActiveTabId(nextTab.id)
            navigateRef.current(nextTab.path)
          }
        }
        return newTabs
      })
    })
    return () => { unsubscribe?.() }
  }, [activeTabId, currentSpace])

  // Save sessions per space when tabs or active tab changes (debounced 300ms).
  // Secondary windows (tear-off) don't save to avoid overwriting the primary window's session.
  // Debounce batches rapid changes (e.g. rapid tab switching, bulk close) into one write.
  useEffect(() => {
    if (isSecondaryWindow.current) return
    if (tabs.length === 0) return

    const timer = setTimeout(() => {
      // Group tabs by lifeSpace and save each group separately
      const spaceGroups = new Map<string, Tab[]>()
      tabs.forEach(tab => {
        const group = spaceGroups.get(tab.lifeSpace) || []
        group.push(tab)
        spaceGroups.set(tab.lifeSpace, group)
      })

      spaceGroups.forEach((spaceTabs, spaceId) => {
        // For the active space: use current activeTabId
        // For other spaces: use in-memory tracking
        const activeIdForSpace = spaceId === currentSpace
          ? activeTabId
          : activeTabPerSpaceRef.current[spaceId] || null
        saveSession(spaceId, spaceTabs, activeIdForSpace)
      })
    }, 300)

    return () => clearTimeout(timer)
  }, [tabs, activeTabId, currentSpace, saveSession])

  // After sync completes, validate that all project tabs reference existing local projects.
  // Tabs restored from localStorage may point to projects that were never synced to this device.
  useEffect(() => {
    const handleSyncComplete = async () => {
      const projectTabs = tabsRef.current.filter(t => t.path.match(/\/project\/([^/]+)/))
      if (projectTabs.length === 0) return

      const projectIds = projectTabs.flatMap(t => {
        const match = t.path.match(/\/project\/([^/]+)/)
        return match ? [match[1]] : []
      })

      const existingProjects = await db.projects.where('id').anyOf(projectIds).toArray()
      const existingIds = new Set(existingProjects.map(p => p.id))

      const orphanedTabs = projectTabs.filter(t => {
        const match = t.path.match(/\/project\/([^/]+)/)
        return match && !existingIds.has(match[1])
      })

      if (orphanedTabs.length > 0) {
        console.log(`[TabManager] closing ${orphanedTabs.length} orphaned project tab(s): ${orphanedTabs.map(t => `"${t.title}"`).join(', ')}`)
        const orphanedIds = new Set(orphanedTabs.map(t => t.id))
        const remaining = tabsRef.current.filter(t => !orphanedIds.has(t.id))
        setTabs(remaining)

        // If active tab was orphaned, switch to next available (outside setTabs to avoid render-during-render)
        if (activeTabIdRef.current && orphanedIds.has(activeTabIdRef.current)) {
          const spaceTabs = remaining.filter(t => t.lifeSpace === currentSpace)
          const nextTab = spaceTabs[0]
          if (nextTab) {
            setActiveTabId(nextTab.id)
            navigateRef.current(nextTab.path)
          }
        }
      }
    }

    window.addEventListener('sync:complete', handleSyncComplete)
    return () => window.removeEventListener('sync:complete', handleSyncComplete)
  }, [currentSpace])

  return {
    tabs: visibleTabs,
    activeTab,
    isSecondaryWindow: isSecondaryWindow.current,
    secondaryReady,
    switchToSpace,
    openTab,
    openPreviewTab,
    promotePreviewTab,
    promotePreviewBySpace,
    closeTab,
    setActiveTab,
    pinTab,
    updateTab,
    navigateActiveTab,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
    duplicateTab,
    closeOtherTabs,
    closeTabsToRight,
    closeTabsToLeft,
    closeAllTabs,
    closeMultipleTabs,
    reorderTabs,
    getDefaultTabForLifeSpace
  }
}
